<template lang="pug">
  b-modal#resetPassword(
    ref="resetPassword"
    :title="$t('ui.combine.edit_password')"
    centered
    no-fade
    no-close-on-backdrop
    :busy="loading"
    size="sm"
    @ok="done"
    @show="reset"
    @shown="shown"
  )
    b-form(novalidate :disabled="!submission.success" @submit.stop.prevent="submit")
      .form-group
        label {{ $t("ui.account_id") }}
        DebounceInput(
          type="text"
          ref="account_id"
          :placeholder='$t("ui.account_id")'
          :value="accountId.toUpperCase()"
          readonly
        )
      .form-group
        .d-flex.align-items-center.justify-content-between.mb-1
          label.mb-0 {{ $t("ui.new_password") }}
          small(v-if="new_password" :class="passwordStrength.badgeClass") {{ passwordStrength.strength }} ({{ passwordStrength.score }}/100)
        b-input-group.input-group-sm
          DebounceInput(
            :type=" (isPWShown) ? 'password' : 'text'"
            ref="new_password"
            :dirty="$v.new_password.$dirty"
            :error="$v.new_password.$error"
            :placeholder='$t("ui.new_password")'
            v-model.trim.lazy="$v.new_password.$model"
            :maxLength="$v.new_password.$params.maxLength.max"
            :disabled="loading"
          )
          .input-group-append(style="cursor: pointer;")
            b-button.btn.btn-outline-secondary.btn-sm(:pressed.sync="isPWShown" style="border-top-right-radius: 0;border-bottom-right-radius: 0;")
              i.fa(:class=" (isPWShown) ? 'icon-eye-open fa-eye' : 'icon-eye-close fa-eye-slash'")
            PasswordRules
          ValidateResult(:value="$v.new_password" :name="$t('ui.new_password')" :errors="newPasswordErrors")
      .form-group
        label {{ $t("ui.confirm_password") }}
        b-input-group.input-group-sm 
          DebounceInput(
            :type=" (isCPWShown) ? 'password' : 'text'"
            ref="confirm_password"
            :dirty="$v.confirm_password.$dirty"
            :error="$v.confirm_password.$error"
            :placeholder='$t("ui.confirm_password")'
            v-model.trim.lazy="$v.confirm_password.$model"
            :disabled="loading"
          )
          .input-group-append(style="cursor: pointer;")
            b-button.btn.btn-outline-secondary.btn-sm(:pressed.sync="isCPWShown" style="border-top-right-radius: 3px;border-bottom-right-radius: 3px;")
              i.fa(:class=" (isCPWShown) ? 'icon-eye-open fa-eye' : 'icon-eye-close fa-eye-slash'")
          ValidateResult(:value="$v.confirm_password" :name="$t('ui.confirm_password')" :name2="$t('ui.new_password')")
      .form-group
        b-alert.alert-sm.animated.flash(:show="!submission.success" variant="danger") {{ submission.status }}
    div(slot="modal-cancel")
      SubmitButton(:loading="loading" icon="fa fa-times" :text="$t('ui.cancel')" isModal=true)
    div(slot="modal-ok")
      SubmitButton(:loading="loading" icon="fa fa-save" :text="$t('ui.save')" isModal=true)
</template>

<script>
import { required, minLength, maxLength, sameAs, helpers } from "vuelidate/lib/validators";
import config from "@/config";
import SubmitButton from "@/library/SubmitButton";
import ValidateResult from "@/library/ValidateResult";
import DebounceInput from "@/library/DebounceInput";
import PasswordRules from "@/library/PasswordRules";

// const containAlphaNum = helpers.regex("containAlphaNum", /^(?=.*\d)(?=.*[a-zA-Z])/);

export default {
  components: {
    SubmitButton,
    ValidateResult,
    DebounceInput,
    PasswordRules
  },
  props: {
    accountId: "",
    accountType: ""
  },
  data() {
    return {
      isPWShown: true,
      isCPWShown: true,
      new_password: "",
      confirm_password: "",
      newPasswordErrors: [],
      passwordStrength: { score: 0, strength: "", badgeClass: "text-muted" },
      loading: false,
      submission: {
        success: true,
        status: ""
      }
    };
  },
  validations() {
    return {
      new_password: {
        required,
        valid: function(value) {
          if (!helpers.req(value)) return true;
          return this.$helpers.validatePassword(value).isValid;
        },
        // minLength: minLength(8),  123 
        maxLength: maxLength(Number(this.accountType) === 1 ? 20 : 50)
      },
      confirm_password: {
        required,
        sameAs: sameAs("new_password")
      }
    };
  },
  watch: {
    new_password(value) {
      if (!value) {
        this.newPasswordErrors = [];
        this.passwordStrength = { score: 0, strength: "", badgeClass: "text-muted" };
        return;
      }
      const result = this.$helpers.validatePassword(value);
      this.newPasswordErrors = result.errors;
      const strengthStyles = {
        Weak: { badgeClass: "text-danger" },
        Fair: { badgeClass: "text-warning" },
        Strong: { badgeClass: "text-primary" },
        "Very Strong": { badgeClass: "text-success" }
      };
      const style = strengthStyles[result.strength] || { badgeClass: "text-muted" };
      this.passwordStrength = {
        score: result.score,
        strength: result.strength,
        badgeClass: style.badgeClass
      };
    }
  },
  methods: {
    done: function(evt) {
      evt.preventDefault();
      this.submit();
    },
    submit: function() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.loading = true
        this.$swal({
          title: this.$t("ui.status"),
          text: this.$t("message.update_account_password_confirm"),
          type: "warning",
          showCancelButton: true,
          allowOutsideClick: false
        }).then(result => {
          if (result.value) {
            this.updatePassword()
          } else if (
            // Read more about handling dismissals
            result.dismiss === 'cancel'
          ) {
            this.loading = false
            this.$swal({
              title: this.$t("ui.cancel"),
              text: this.$t("message.update_account_password_cancel"),
              type: "error",
              allowOutsideClick: false
            }).then(isConfirm => {
              if (isConfirm) {
                this.cancel();
              }
            });
          }
        });
      }
    },
    updatePassword: function() {
      let target_login_id = this.accountId;
      let new_password = this.new_password;
      this.loading = true;
      this.$store
        .dispatch("user/resetPassword", { target_login_id, new_password })
        .then(response => {
          this.loading = false;
          this.submission.success = response.success;
          if (this.submission.success == true) {
            this.loading = false
            this.$swal({
              title: this.$t("ui.succeed"),
              text: this.$t("message.update_account_password_success"),
              type: "success",
              allowOutsideClick: false
            }).then(isConfirm => {
              if (isConfirm) {
                this.cancel();
              }
            });
          }
        })
        .catch(response => {
          console.log("Failed:resetPassword", response)
          this.loading = false;
          this.submission.success = false
          this.submission.status = this.$helpers.getErrorMessage(
            response && response.errorTag,
            "update_password_failed"
          )
        });
    },
    reset: function() {
      this.submission.success = true;
      this.submission.status = "";
      this.new_password = "";
      this.confirm_password = "";
      this.passwordStrength = { score: 0, strength: "", badgeClass: "text-muted" };
      this.newPasswordErrors = [];
    },
    shown: function() {
      this.$nextTick(() => {
        this.$v.$reset();
      });
    },
    cancel: function() {
      this.$refs.resetPassword.hide()
    }
  }
};
</script>
